<template>
  <MainLayout>
    <ContentPageHeader functionName="買取査定管理" pageName="買取履歴一覧">
    </ContentPageHeader>
    
    <div class="search-section">
      <div class="search-form">
        <FormDateField 
          label="買取日（From）"
          id="purchaseDateFrom" 
          v-model="searchParams.purchaseDateFrom"
        />
        
        <FormDateField 
          label="買取日（To）"
          id="purchaseDateTo" 
          v-model="searchParams.purchaseDateTo"
        />
        
        <FormSelectSearchField 
          label="買取店舗" 
          id="purchaseStore" 
          v-model="searchParams.purchaseStore"
          :options="storeOptions"
          size="small"
        />
        
        <FormTextField 
          label="備考欄検索" 
          id="notes" 
          v-model="searchParams.notes" 
          size="medium"
        />
        
        <div class="search-buttons">
          <button class="btn btn-secondary" @click="resetSearch">リセット</button>
          <button class="btn btn-primary" @click="search">検索</button>
        </div>
      </div>
    </div>
    
    <div class="search-result">
      <FormListTable
          :items="sortedProducts"
          :columns="columns"
          :actions="actions"
          :perPage="perPage"
          :page="currentPage"
          :totalItems="totalCount"
          :actionColumnWidth="180"
          :visibleColumns="visibleColumns"
          :emptyMessage="'買取履歴がありません'"
          :sortKey="sortKey"
          :sortOrder="sortOrder"
          @page-change="onPageChange"
          @action="onAction"
          @sort="sortBy"
          @visible-columns-change="onVisibleColumnsChange"
        >
          <!-- カスタムヘッダーテンプレート -->
          <template v-for="col in columns.filter(col => col.sortable)" :key="`header-${col.key}`" #[`header-${col.key}`]>
            <div class="column-header" @click="sortBy(col.key, col.originalKey)">
              {{ col.label }}
              <span class="sort-icon">
                <i v-if="sortKey === col.key && sortOrder === 'asc'" class="fa fa-sort-up" aria-hidden="true"></i>
                <i v-else-if="sortKey === col.key && sortOrder === 'desc'" class="fa fa-sort-down" aria-hidden="true"></i>
                <i v-else class="fa fa-sort" aria-hidden="true"></i>
              </span>
            </div>
          </template>
      </FormListTable>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormSelectSearchField from '@/components/form/FormSelectSearchField.vue'
import FormDateField from '@/components/form/FormDateField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import apiService from '@/services/service.js'
import { showLoading, hideLoading } from '@/utils/loadingEventBus';
import constants from '../../constants/constants.json';

export default {
  name: 'PurchaseHistoryView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormSelectSearchField,
    FormDateField,
    FormListTable
  },
  data() {
    return {
      // 検索パラメータ
      searchParams: {
        purchaseDateFrom: '',
        purchaseDateTo: '',
        purchaseStore: '',
        notes: ''
      },
      // 店舗オプション (修正: storeOptionsを追加)
      storeOptions: [],
      // ページング情報
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      // テーブル設定
      // テーブルカラム定義
      columns: [
        { key: 'id', label: 'ID', sortable: true, minWidth: 60, maxWidth: 80 },
        { key: 'purchaseDate', label: '買取日', sortable: true, minWidth: 130, maxWidth: 150 },
        { key: 'amount', label: '買取金額', sortable: true, minWidth: 100, maxWidth: 150 },
        { key: 'staffName', label: '担当者', sortable: true, minWidth: 120, maxWidth: 180 },
        { key: 'notes', label: '備考', sortable: true, minWidth: 150, maxWidth: 600 },
        { key: 'isSatisfiedCount', label: '不成立数', sortable: true, minWidth: 100, maxWidth: 140 },
      ],
      // 表示カラム設定
      visibleColumns: [],
      // ソート状態
      sortKey: null,
      sortOrder: 'asc',
      currentSortOriginalKey: null, // マスタデータのID値でソートするための元キー
      // 操作ボタン定義
      actions: [
        {
          label: '',
          icon: 'fa-address-card',
          buttonClass: 'btn-primary', //主要機能のボタン青色で表示
          action: (item) => this.editCustomer(item),
          condition: (item) => this.hasPermission(item),
          disabled: item => (
            this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.storeId)
            )
        },
        {
          label: '',
          icon: 'fa-edit',
          buttonClass: 'btn-primary', //主要機能のボタン青色で表示
          action: (item) => this.editPurchase(item),
          condition: (item) => this.hasPermission(item),
          disabled: item => (
            this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.storeId)
            )
        },
        { 
          label: '', 
          icon: 'fa-file-alt',
          buttonClass: 'btn-secondary', //補助機能のボタン緑色で表示
          action: (item) => this.viewDetail(item),
          condition: (item) => this.hasPermission(item),
          disabled: item => (
            this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.storeId)
            )
        },
        {
          label: '',
          icon: 'fa-trash',
          buttonClass: 'btn-danger', //例外機能のボタン赤色で表示
          action: (item) => this.deletePurchase(item),
          condition: (item) => this.hasPermission(item),
          disabled: item => (
            this.$store.getters['auth/currentUser']?.authority == constants.Authority.STAFF.id || 
            (this.$store.getters['auth/currentUser']?.authority != constants.Authority.ADMIN.id && 
              !this.isCurrentUserStore(item.storeId))
            )
        },
      ],
      // 買取履歴サンプルデータ (APIから取得するように実装)
      purchases: []
    }
  },
  methods: {
    // 検索条件を保存
    saveSearchConditions() {
      const conditions = {
        searchParams: { ...this.searchParams },
        currentPage: this.currentPage,
        perPage: this.perPage,
        sortKey: this.sortKey,
        sortOrder: this.sortOrder,
        currentSortOriginalKey: this.currentSortOriginalKey,
        visibleColumns: [...this.visibleColumns]
      };
      localStorage.setItem('purchaseHistorySearchConditions', JSON.stringify(conditions));
    },
    
    // 検索条件を復元
    loadSearchConditions() {
      const saved = localStorage.getItem('purchaseHistorySearchConditions');
      if (saved) {
        const conditions = JSON.parse(saved);
        this.searchParams = { ...conditions.searchParams };
        this.currentPage = conditions.currentPage || 0;
        this.perPage = conditions.perPage || 50;
        this.sortKey = conditions.sortKey || null;
        this.sortOrder = conditions.sortOrder || 'asc';
        this.currentSortOriginalKey = conditions.currentSortOriginalKey || null;
        if (conditions.visibleColumns) {
          this.visibleColumns = [...conditions.visibleColumns];
        }
      }
    },
    
    async loadStoreOptions() {
      try {
        const response = await apiService.getStoreList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
        
        this.storeOptions = response.data.stores.map(store => ({
          value: store.id,
          label: store.storeName
        }));
      } catch (error) {
        console.error('店舗情報の取得に失敗しました:', error);
        alert('店舗情報の取得中にエラーが発生しました。');
      }
    },
    // 検索処理
    async searchPurchases(page) {
      try {
        showLoading();
        // ページングの更新
        if (page !== undefined) {
          this.currentPage = page;
        } else {
          this.currentPage = 0;
        }
        // 検索条件の有無に応じてAPIパラメータを調整
        const params = new URLSearchParams();
        
        if (this.searchParams.purchaseDateFrom) {
          params.append('purchaseDateFrom', this.searchParams.purchaseDateFrom);
        }
        
        if (this.searchParams.purchaseDateTo) {
          params.append('purchaseDateTo', this.searchParams.purchaseDateTo);
        }
        
        if (this.searchParams.purchaseStore) {
          params.append('purchaseStore', this.searchParams.purchaseStore);
        }
        
        if (this.searchParams.notes) {
          params.append('note', this.searchParams.notes);
        }
        
        // ページングパラメータ
        params.append('page', this.currentPage);
        params.append('perPage', this.perPage);
        
        // APIから買取履歴を取得する実装
        const response = await apiService.getSearchPurchases(params);
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
        if (response.data == null) {
          this.purchases = [];
          this.totalCount = 0;
          return;
        }

        // 非同期処理を並列で実行
        this.purchases = response.data.purchases.map((item) => ({
          id: item.id,
          purchaseDate: item.purchaseDate,
          amount: this.formatCurrency(item.amount),
          staffName: item.staffName,
          notes: item.note,
          isSatisfiedCount: item.isSatisfiedCount,
          storeId: item.storeId
        }));
        
        // ページング情報を更新
        this.totalCount = response.data.totalCount || 0;
        this.currentPage = response.data.page || 0;
        this.perPage = response.data.perPage || 50;

      } catch (error) {
        console.error('買取履歴の取得に失敗しました:', error);
        alert('買取履歴の取得中にエラーが発生しました。');
        this.purchases = [];
        this.totalCount = 0;
      } finally {
        hideLoading();
      }
      
      // 検索条件を保存
      this.saveSearchConditions();
    },
    
    // 金額フォーマット（数値 → ¥XX,XXX）
    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      
      // 数値に変換
      const numValue = parseInt(value);
      if (isNaN(numValue)) return value;
      
      // 3桁区切りでフォーマット
      return '¥' + numValue.toLocaleString();
    },
    
    // 検索ボタン押下時の処理
    search() {
      // 現在の検索条件で検索を実行
      this.searchPurchases();
    },
    
    // 検索条件リセット
    resetSearch() {
      this.searchParams = {
        purchaseDateFrom: '',
        purchaseDateTo: '',
        purchaseStore: '',
        notes: ''
      };
      this.searchPurchases();
    },
    
    // ページ変更ハンドラ
    onPageChange(page) {
      // ページ変更時にAPIリクエストする
      // APIは0-indexedなので、1-indexedから変換する
      this.searchPurchases(page - 1);
    },
    
    // カラムヘッダークリック時のソート処理
    sortBy(key, originalKey) {
      // 同じカラムをクリックした場合は昇順/降順を切り替え
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        // 異なるカラムをクリックした場合は昇順にリセット
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
      
      // マスタデータの場合は元のID値でソート
      this.currentSortOriginalKey = originalKey || null;
    },
    
    // 表示カラム変更時の処理
    onVisibleColumnsChange(columns) {
      this.visibleColumns = [...columns];
      // ローカルストレージに保存して次回アクセス時も設定を維持
      localStorage.setItem('purchaseHistoryVisibleColumns', JSON.stringify(columns));
    },
    
    // アクションハンドラ
    onAction({ action, item }) {
      console.log(`アクション ${action} が ${item.id} に対して実行されました`);
    },

    editCustomer(item) {
      // 編集可能な状態でPurchaseRegisterView画面へ遷移
      this.$router.push({ name: 'PurchaseRegister', params: { id: item.id } });
    },

    editPurchase(item) {
      // 編集可能な状態でPurchaseDataRegisterView画面へ遷移
      sessionStorage.setItem('legitimateNavigation', 'PurchaseDataRegister');
      this.$router.push({ name: 'PurchaseDataRegister', params: { id: item.id } });
    },

    viewDetail(item) {
      // 詳細表示画面へ遷移
      sessionStorage.setItem('legitimateNavigation', 'PurchaseDetailView');
      this.$router.push({ name: 'PurchaseDetailView', params: { id: item.id } });
    },

    async deletePurchase(item) {
      // 削除処理
      if (confirm('この買取データを削除してもよろしいですか？')) {
        try {
          showLoading();
          await apiService.deletePurchase(item.id);
          this.search();
        } catch (error) {
          console.error('削除に失敗しました:', error);
          alert('削除中にエラーが発生しました。');
        } finally {
          hideLoading();
        }
      }
    },
    
    // 権限チェック
    hasPermission(item) {
      // 管理者の場合はすべてのボタンを活性にする
      if (this.userAuthority === constants.Authority.ADMIN.id) {
        return true;
      }
      
      // 店長またはスタッフの場合は、storeIdが所属店舗と一致するデータのみボタンを活性にする
      return item.storeId === this.userStoreId;
    },
    // 日付フォーマット
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },

    async fetchData() {
      try {
        showLoading();
        // 初期データの読み込み
        this.loadStoreOptions();

        // 初期検索実行
        await this.searchPurchases();
        
      } catch (error) {
        console.error('データの取得に失敗しました:', error);
        alert('データの取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },

    // ログインユーザーの店舗かどうか判定
    isCurrentUserStore(storeId) {
      const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
      return userStoreId === storeId;
    },
  },
  computed: {
    // ユーザー権限の取得
    userAuthority() {
      return this.$store.getters['auth/currentUser']?.authority || constants.Authority.STAFF.id;
    },
    
    // ユーザーの所属店舗ID
    userStoreId() {
      return this.$store.getters['auth/currentUser']?.storeId || null;
    },
    

    
    // ソート済み買取履歴一覧
    sortedProducts() {
      if (!this.sortKey) {
        return this.purchases;
      }
      
      return [...this.purchases].sort((a, b) => {
        let aValue = a[this.sortKey];
        let bValue = b[this.sortKey];
        
        // マスタデータの場合は元のID値でソート
        if (this.currentSortOriginalKey) {
          aValue = a[this.currentSortOriginalKey];
          bValue = b[this.currentSortOriginalKey];
        }

        // 金額カラムの場合は通貨記号を削除して数値として比較
        const monetaryColumns = ['amount'];
        if (monetaryColumns.includes(this.sortKey)) {
          // 通貨記号と区切り文字を削除して数値に変換
          const parseMonetaryValue = (value) => {
            if (!value) return 0;
            return parseInt(String(value).replace(/[¥,]/g, ''));
          };

          const aNum = parseMonetaryValue(aValue);
          const bNum = parseMonetaryValue(bValue);
          return this.sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // 数値型の場合は数値として比較
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return this.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // 文字列型の場合は文字列として比較
        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';
        
        const comparison = String(aValue).localeCompare(String(bValue));
        return this.sortOrder === 'asc' ? comparison : -comparison;
      });
    }
  },
  async mounted() {
    // 検索条件を復元
    this.loadSearchConditions();
    
    await this.fetchData();

    // 表示カラム設定の復元
    const savedColumns = localStorage.getItem('purchaseHistoryVisibleColumns');
    if (savedColumns) {
      try {
        this.visibleColumns = JSON.parse(savedColumns);
      } catch (e) {
        console.error('表示カラム設定の復元に失敗しました:', e);
        // デフォルトで全カラムを表示
        this.visibleColumns = this.columns.map(col => col.key);
      }
    } else {
      // デフォルトで全カラムを表示
      this.visibleColumns = this.columns.map(col => col.key);
    }
  }
}

</script>

<style scoped>
.search-section {
  background-color: #FFF;
  margin: 5px;
  border-radius: 4px;
  padding: 15px;
}

.search-form {
  display: flex;
  flex-direction: column;
}

.search-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}

.search-result {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  margin: 5px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.table th {
  font-weight: bold;
  background-color: #195983;
  color: white;
}

.table-responsive {
  overflow-x: auto;
}

.text-center {
  text-align: center;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table td > .btn {
  margin-right: 15px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
}

.page-info {
  font-weight: bold;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.column-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sort-icon {
  margin-left: 0.5rem;
}
</style>
